/**
 * gemscape-cache.js
 *
 * Module-level cache for fetched & parsed gemscape SVGs.
 * Handles: fetch → sanitize → parse → classify → build markup → cache.
 *
 * Classification (base/top bucketing) is delegated to
 * gemscape-classify.js — an intermediary module that will be removed
 * once the server-side pipeline emits pre-tagged SVGs.
 *
 * The cache is shared across all Waveform instances so each SVG
 * is only fetched and processed once per session.
 *
 * Shape fractions (horizontal 0–1 position) are NOT computed here.
 * They are computed once from live DOM `getBoundingClientRect()` when
 * the active overlay first mounts — see `computeLiveFractions()` and
 * the useEffect in index.jsx.
 */
import DOMPurify from "dompurify";
import { classifyAndTagShapes } from "./gemscape-classify";

// ─── S3 → Dev Proxy rewrite ─────────────────────────────────────────
const S3_ORIGIN =
  "https://blue-dot-sessions-staging.s3.us-west-2.amazonaws.com";

function resolveUrl(url) {
  if (
    process.env.NODE_ENV === "development" &&
    url &&
    url.startsWith(S3_ORIGIN)
  ) {
    return url.replace(S3_ORIGIN, "/gemscape-proxy");
  }
  return url;
}

// ─── Cache (LRU) ────────────────────────────────────────────────────
// Map<url, { markup, viewBox }> — one entry per unique SVG URL.
// Insertion order tracks recency: most-recently-used entry is last.
// When the cap is reached the oldest (first) entry is evicted.
const MAX_CACHE = 80;
const cache = new Map();

/** Promote `key` to most-recently-used position. */
function touchCache(key) {
  const val = cache.get(key);
  if (val !== undefined) {
    cache.delete(key);
    cache.set(key, val);
  }
}

/** Insert into cache, evicting the LRU entry if at capacity. */
function cacheSet(key, value) {
  if (cache.has(key)) cache.delete(key); // promote
  cache.set(key, value);
  if (cache.size > MAX_CACHE) {
    const oldest = cache.keys().next().value; // first = LRU
    cache.delete(oldest);
  }
}

// Map<url, Promise> — deduplicates in-flight fetches
const pending = new Map();

/**
 * Get a parsed gemscape from cache, or fetch + parse + cache it.
 * Returns: { markup: string, viewBox: string }
 */
export async function getGemscape(url) {
  if (!url) return null;
  if (cache.has(url)) {
    touchCache(url);
    return cache.get(url);
  }

  if (pending.has(url)) return pending.get(url);

  const promise = fetchAndParse(url);
  pending.set(url, promise);

  try {
    const result = await promise;
    cacheSet(url, result);
    return result;
  } catch (err) {
    console.warn(`[gemscape] Failed to load ${url}:`, err.message);
    return null;
  } finally {
    pending.delete(url);
  }
}

/** Check if a URL is already cached (synchronous). */
export function isCached(url) {
  return cache.has(url);
}

// ─── Live Fraction Computation ──────────────────────────────────────
// Called once when the ActiveWaveform first mounts into the real DOM.
// Uses getBoundingClientRect() to derive pixel-space 0–1 fractions
// relative to the container rect.

/**
 * Stamp `data-fraction-start` / `data-fraction-end` on every top-layer shape.
 * No-op if already computed for this mount (checks a sentinel attribute).
 */
export function computeLiveFractions(container) {
  if (!container) return;

  const svg = container.querySelector("svg.gemscape-active");
  if (!svg) return;

  // Already computed for this mount — skip
  if (svg.dataset.fractionsComputed) return;
  svg.dataset.fractionsComputed = "1";

  // Use the container's pixel rect as the reference frame — this is
  // the same rect the mouse handler divides by, so fractions match.
  const containerRect = container.getBoundingClientRect();
  if (!containerRect.width) return;

  const shapes = container.querySelectorAll(".layer-top");
  shapes.forEach(shape => {
    try {
      const r = shape.getBoundingClientRect();
      const start = (r.left - containerRect.left) / containerRect.width;
      const end = (r.right - containerRect.left) / containerRect.width;
      shape.dataset.fractionStart = start.toFixed(4);
      shape.dataset.fractionEnd = end.toFixed(4);
    } catch (_) {
      // getBoundingClientRect can fail on hidden elements — skip
    }
  });
}

// ─── Ancestor Transform Helper ──────────────────────────────────────
// Used by dual-group base markup to preserve shape positioning.

function getAncestorTransform(el) {
  const transforms = [];
  let node = el.parentElement;
  while (node && node.tagName !== "svg") {
    const t = node.getAttribute("transform");
    if (t) transforms.unshift(t);
    node = node.parentElement;
  }
  return transforms.length > 0 ? transforms.join(" ") : null;
}

// ─── Fetch + Parse ──────────────────────────────────────────────────

async function fetchAndParse(url) {
  const fetchUrl = resolveUrl(url);
  const response = await fetch(fetchUrl);
  if (!response.ok) throw new Error(`SVG fetch failed: ${response.status}`);
  const rawText = await response.text();

  const cleanSvg = DOMPurify.sanitize(rawText, {
    USE_PROFILES: { svg: true, svgFilters: true },
    ADD_TAGS: ["use"]
  });

  const parser = new DOMParser();
  const doc = parser.parseFromString(cleanSvg, "image/svg+xml");
  const svgEl = doc.querySelector("svg");

  if (!svgEl) throw new Error("Invalid SVG — no <svg> root element");

  const viewBox = extractViewBox(svgEl);

  // Classify shapes into base/top and tag them with data attributes.
  // This call delegates to gemscape-classify.js and will be removed once
  // the server pipeline emits pre-tagged SVGs.
  classifyAndTagShapes(svgEl);

  const serializer = new XMLSerializer();

  // ── Build dual-group base progress markup ─────────────────────────
  let dualGroupMarkup = "";
  const baseShapes = svgEl.querySelectorAll(".layer-base");
  if (baseShapes.length > 0) {
    const [, , , vbH] = viewBox.split(" ").map(Number);
    let baseMarkup = "";
    baseShapes.forEach(el => {
      const shapeStr = serializer.serializeToString(el);
      const ancestorTx = getAncestorTransform(el);
      baseMarkup += ancestorTx
        ? `<g transform="${ancestorTx}">${shapeStr}</g>`
        : shapeStr;
    });

    const progressDefs = `<defs><clipPath id="__BASE_CLIP_ID__"><rect class="base-progress-rect" x="0" y="0" width="0" height="${vbH}" /></clipPath></defs>`;
    const grayscaleGroup = `<g class="base-unplayed">${baseMarkup}</g>`;
    const colorGroup = `<g class="base-played" clip-path="url(#__BASE_CLIP_ID__)">${baseMarkup}</g>`;
    dualGroupMarkup = progressDefs + grayscaleGroup + colorGroup;

    // Hide originals so main serialization doesn't render them
    baseShapes.forEach(el => {
      el.setAttribute("display", "none");
    });
  }

  // ── Serialize main markup (base shapes now hidden) ────────────────
  let markup = "";
  Array.from(svgEl.children).forEach(child => {
    markup += serializer.serializeToString(child);
  });

  // Prepend base dual-groups so they paint BEHIND top shapes
  markup = dualGroupMarkup + markup;

  return { markup, viewBox };
}

// ─── ViewBox Extraction ─────────────────────────────────────────────

function extractViewBox(svgEl) {
  const vb = svgEl.getAttribute("viewBox");
  if (vb) return vb;

  const w = parseFloat(svgEl.getAttribute("width")) || 100;
  const h = parseFloat(svgEl.getAttribute("height")) || 100;
  return `0 0 ${w} ${h}`;
}
